import LogoutIcon from "@mui/icons-material/Logout";
import CheckIcon from "@mui/icons-material/Check";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import { useState, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';

import { useCurrentOrg, useOrganization } from "~/providers/GraphqlProvider";

import {
  IconButton,
  Tooltip,
} from "@mui/material";
import { useAuth } from "~/state/auth";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import PermissionOrHide from "../common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";
import { useQuery } from "@apollo/client";
import { CommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import { COMMAND_INDEX_PROVIDERS_QUERY } from "~/queries/forms";
import OptimizerReplayMenuItem from "./OptimizerReplayMenuItem";
import PersistFhirQueryMenuItem from "./PersistFhirQueryMenuItem";

const UserActionsMenu = () => {
  const { logout, userEmail } = useAuth();
  const navigate = useNavigate();
  const {organizationId, setOrganizationId, availableOrganizations} = useOrganization();

  // https://mui.com/material-ui/react-menu/
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [optimizerReplayOpen, setOptimizerReplayOpen] = useState(false);
  const [persistFhirQueryOpen, setPersistFhirQueryOpen] = useState(false);
  const [orgsOpen, setOrgsOpen] = useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setOrgsOpen(false);
  };

  const cognitoLogout = ():void => {
    logout();
  }

  const { timeFormatPreference, toggleTimeFormat } = useZonedDateFormat();

  const orgs = (availableOrganizations || []).map(({id, name}) => (<MenuItem dense key={id} onClick={() => {
    setOrganizationId?.(id)
    navigate("/command");
  }}>
    {id === organizationId ? <ListItemIcon>
      <CheckIcon fontSize="small"/>
    </ListItemIcon> : null}
    <ListItemText inset={id !== organizationId}>{name}</ListItemText>
  </MenuItem>))

  return <div>
    <Tooltip title={userEmail}>
      <IconButton
        color="inherit"
        id="user-actions-button"
        aria-controls={open ? 'user-actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <AccountCircleIcon />
      </IconButton>
    </Tooltip>

    <Menu
      id="user-action-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      slotProps={{
        list: { 'aria-labelledby': 'user-actions-button' },
        paper: { sx: { width: '240px' }},
      }}
    >
      <MenuItem disabled>
        <ListItemText>Settings</ListItemText>
      </MenuItem>
      <MenuItem onClick={toggleTimeFormat} sx={{ pl: 2 }}>
        <ListItemIcon>
          <AccessTimeIcon fontSize="small"/>
        </ListItemIcon>
        <ListItemText>Military Time</ListItemText>
        <Switch
          edge="end"
          checked={timeFormatPreference === 'military'}
          size="small"
        />
      </MenuItem>
      <PermissionOrHide permission={Permission.copyDemoData}>
        <MenuItem onClick={() => {
          navigate("/demo-data");
          handleClose();
        }} sx={{ pl: 2 }}>
          <ListItemText>Copy Demo Data</ListItemText>
        </MenuItem>
      </PermissionOrHide>
      <PermissionOrHide permission={Permission.debugApps}>
        <MenuItem onClick={() => {
          setOptimizerReplayOpen(true);
          handleClose();
        }} sx={{ pl: 2 }}>
          <ListItemText>Replay Optimizer JSON</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          setPersistFhirQueryOpen(true);
          handleClose();
        }} sx={{ pl: 2 }}>
          <ListItemText>Persist FHIR Query</ListItemText>
        </MenuItem>
      </PermissionOrHide>

      {orgs.length > 1 ? <>
          <MenuItem onClick={() => setOrgsOpen((prev) => !prev)}>
            <ListItemText>Switch organization</ListItemText>
            {orgsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </MenuItem>
          <Collapse in={orgsOpen} timeout="auto" unmountOnExit>
            {orgs}
          </Collapse>
        </>
      : null}

      <PermissionOrHide permission={Permission.impersonateAnyProvider}>
        <SwitchProviders />
      </PermissionOrHide>

      <Divider sx={{ my: 1 }} />
      <MenuItem onClick={() => cognitoLogout()}>
        <ListItemIcon>
          <LogoutIcon fontSize="small"/>
        </ListItemIcon>
        <ListItemText>Sign out</ListItemText>
      </MenuItem>
    </Menu>
    <OptimizerReplayMenuItem
      open={optimizerReplayOpen}
      onClose={() => setOptimizerReplayOpen(false)}
    />
    <PersistFhirQueryMenuItem
      open={persistFhirQueryOpen}
      onClose={() => setPersistFhirQueryOpen(false)}
    />
  </div>
}

export default UserActionsMenu;

const SwitchProviders = () => {
  const [open, setOpen] = useState(false);
  const { data } = useQuery<CommandIndexProvidersQuery>(COMMAND_INDEX_PROVIDERS_QUERY, {
    variables: {
      active: true,
    }
  });
  const currentProviderId = useCurrentOrg()?.providerId;

  const { overrideProviderId } = useOrganization();
  const navigate = useNavigate();

  return <>
    <MenuItem onClick={() => setOpen((prev) => !prev)}>
      <ListItemText>Switch provider</ListItemText>
      {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
    </MenuItem>
    <Collapse in={open} timeout="auto" unmountOnExit>
      {data?.commandIndexProviders?.providers?.map(provider => <MenuItem dense key={provider.id} onClick={() => {
        overrideProviderId?.(provider.id);
        navigate("/command");
      }}>
        {provider.id === currentProviderId ? <ListItemIcon>
          <CheckIcon fontSize="small"/>
        </ListItemIcon> : null}
        <ListItemText inset={provider.id !== currentProviderId}>{provider.firstName} {provider.lastName}</ListItemText>
      </MenuItem>)}
    </Collapse>
  </>
}
